import { useSyncExternalStore } from 'react'
import { api, Fout } from './api'
import type { Expense, IkAntwoord } from './types'

/* ===========================================================================
 *  Wat de schermen delen
 *
 *  Wie je bent, de stapel die nog niet af is, en de lijst waar je net uit
 *  kwam (voor Overslaan en "door naar de volgende"). Eén kleine winkel met
 *  useSyncExternalStore; meer is het niet.
 * =========================================================================== */

export interface Melding { tekst: string; soort: 'ok' | 'let-op' | 'fout'; id: number }

interface Staat {
  ik: IkAntwoord | null
  werk: { bonnen: Expense[]; op: number } | null
  werkLaden: boolean
  werkFout: string | null
  /** De ids van de lijst waar je een factuur uit opende, in die volgorde. */
  lijst: string[]
  melding: Melding | null
}

let staat: Staat = { ik: null, werk: null, werkLaden: false, werkFout: null, lijst: [], melding: null }
const luisteraars = new Set<() => void>()

function zet(deel: Partial<Staat>) {
  staat = { ...staat, ...deel }
  for (const l of luisteraars) l()
}

export function useStaat<T>(kies: (s: Staat) => T): T {
  return useSyncExternalStore(
    (l) => { luisteraars.add(l); return () => { luisteraars.delete(l) } },
    () => kies(staat),
  )
}

export const lees = () => staat

/* ------------------------------------------------------------------ */

export async function laadIk(): Promise<IkAntwoord> {
  const ik = await api.ik()
  zet({ ik })
  return ik
}

let bezig: Promise<void> | null = null

/** De stapel ophalen. Twee schermen die tegelijk vragen, krijgen dezelfde vraag. */
export function laadWerk(): Promise<void> {
  if (bezig) return bezig
  zet({ werkLaden: true, werkFout: null })
  bezig = api.werk()
    .then((w) => zet({ werk: w, werkLaden: false }))
    .catch((e) => zet({ werkLaden: false, werkFout: e instanceof Fout ? e.message : String(e) }))
    .finally(() => { bezig = null })
  return bezig
}

export function zetLijst(ids: string[]) { zet({ lijst: ids }) }

let meldingNr = 0
export function meld(tekst: string, soort: Melding['soort'] = 'ok') {
  const id = ++meldingNr
  zet({ melding: { tekst, soort, id } })
  window.setTimeout(() => { if (staat.melding?.id === id) zet({ melding: null }) }, soort === 'fout' ? 7000 : 4200)
}
export function wisMelding() { zet({ melding: null }) }

/* De To-do-bakken staan in lib/bakken.ts: zonder React of netwerk, zodat
   dev/todo-nakijken.ts ze kan narekenen. */
export { bakkenVan, type Bakken } from './bakken'

/* ------------------------------------------------------------------ *
 *  Voorkeuren: per toestel, en alles mag ontbreken
 * ------------------------------------------------------------------ */

export interface Voorkeuren {
  /** Het bedrijf dat bij Scannen bovenaan staat. */
  bedrijf?: string
  /** Na tekenen meteen door naar de volgende (zoals het dashboard). */
  doorNaVolgende?: boolean
}

const SLEUTEL = 'tw10.voorkeuren'

export function leesVoorkeuren(): Voorkeuren {
  try {
    const v = JSON.parse(localStorage.getItem(SLEUTEL) ?? '{}') as Voorkeuren
    return { doorNaVolgende: true, ...v }
  } catch {
    return { doorNaVolgende: true }
  }
}

export function bewaarVoorkeuren(v: Voorkeuren) {
  try { localStorage.setItem(SLEUTEL, JSON.stringify(v)) } catch { /* privévenster: dan maar niet onthouden */ }
}
